import React, { useEffect, useState } from 'react';
import { useAttachmentTypeQuery } from 'api';
import { MAX_FILE_SIZE } from 'constants/index';
import { convertEnumsToReadableStrings } from 'utils/helpers';
import { UploadContent, Form, Upload, List } from 'components';
import { Button, Select, Typography, Row, Col, Image } from 'components';
import { CloseIcon, CloseIconLight, DoneIcon } from 'components/common/icons';
import styles from '../../styles.module.scss';

const { Text } = Typography;
const { Dragger } = Upload;

interface IAttachmentForm {
  attachmentType?: string;
  setAttachmentType: (type: string | undefined) => void;
  fileList: File[];
  setFileList: (files: File[]) => void;
  attachments: { type: string; file: File }[];
  setAttachments: (attachments: { type: string; file: File }[]) => void;
}

export const AttachmentForm = React.memo<IAttachmentForm>((props) => {
  const { data: attachmentTypes = [] } = useAttachmentTypeQuery({ entityName: 'LEAD' });
  const { attachmentType, setAttachmentType, fileList, setFileList, attachments, setAttachments } = props;
  const [disabledActions, setDisabledActions] = useState(true);
  const [error, setError] = useState('');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);

  const handleAdd = () => {
    setAttachments([...(attachments as any), { type: attachmentType, file: fileList[0] }]);
    setAttachmentType(undefined);
    setFileList([]);
  };

  const handleFileSizeValidation = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      setFileList([]);
      setError('File exceeds the maximum size of 5 MB.');
      return Upload.LIST_IGNORE;
    }

    const isImage = ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type);
    if (isImage) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target?.result as string);
        setIsPreviewVisible(true);
      };
      reader.readAsDataURL(file);
    }

    setFileList([file]);
    setError('');
    return false;
  };

  const handleDelete = (index: number) => {
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
  };

  const handleCancel = () => {
    setAttachmentType(undefined);
    setFileList([]);
  };

  useEffect(() => {
    setDisabledActions(!fileList?.length);
  }, [fileList, attachmentType]);

  return (
    <>
      {attachments.length > 0 && (
        <List
          dataSource={attachments}
          style={{ marginBottom: 24 }}
          renderItem={(item, index) => (
            <List.Item
              className={styles.list_item}
              actions={[
                <Button
                  key={'add'}
                  size={'small'}
                  type={'link'}
                  icon={<CloseIconLight />}
                  onClick={() => handleDelete(index)}
                />,
              ]}
            >
              <Text ellipsis={{ tooltip: true }} style={{ maxWidth: 400, height: 32, marginTop: 8, marginLeft: 12 }}>
                {item.type && <span className={styles.option_tag}>{convertEnumsToReadableStrings(item.type)}</span>}{' '}
                {item.file.name}
              </Text>
            </List.Item>
          )}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Select
            size={'large'}
            value={attachmentType}
            options={attachmentTypes}
            onChange={setAttachmentType}
            style={{ width: '100%' }}
          />
        </Col>

        <Col span={24}>
          <Form.Item name="file" validateStatus={error ? 'error' : ''} help={error || ''}>
            <Dragger
              accept=".csv,.pdf,.png,.jpg,.jpeg"
              style={{ height: 40 }}
              beforeUpload={handleFileSizeValidation}
              fileList={fileList as any}
              onRemove={() => setFileList([])}
              maxCount={1}
            >
              <UploadContent
                title={'Upload or Drag and Drop File'}
                message={'Max size: 5MB / Format: CSV, PDF, PNG, JPG, JPEG'}
              />
            </Dragger>
          </Form.Item>
        </Col>
        <Col span={24}>
          <Row gutter={4} justify={'end'}>
            <Col>
              <Button
                disabled={disabledActions}
                color="default"
                variant="filled"
                onClick={handleCancel}
                icon={<CloseIcon />}
              />
            </Col>
            <Col>
              <Button disabled={disabledActions} type="primary" icon={<DoneIcon />} onClick={handleAdd} />
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Image Preview Modal (hidden image trigger) */}
      {previewImage && (
        <Image
          src={previewImage}
          style={{ display: 'none' }}
          preview={{
            visible: isPreviewVisible,
            onVisibleChange: (visible) => setIsPreviewVisible(visible),
          }}
        />
      )}
    </>
  );
});
